import { Button } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { chakra, FlexProps } from '@chakra-ui/react';
import { GoalType } from '@common/graphql/generated';
import ButtonWithIcon from '../../common/ButtonWithIcon';
import { ArrowLeft, XClose } from '../../common/icons';
import { Paragraph, Subheader } from '../../common/Typography';
import GoalIconCircle from '../GoalIconCircle';

export interface GoalCardLayoutProps extends FlexProps {
    type: GoalType;
    title: string;
    subtitle?: string;
    onNext: VoidFunction;
    onClose: VoidFunction;
    isNextDisabled?: boolean;
    isNextLoading?: boolean;
    nextButtonText?: string;
    onBack?: VoidFunction;
    backButtonText?: string;
    nextButtonTabIndex?: number;
    isFirstCard?: boolean;
}

const GoalCardLayout = ({
    type,
    title,
    subtitle,
    onNext,
    onClose,
    isNextDisabled,
    isNextLoading,
    onBack,
    nextButtonText = 'Next',
    backButtonText = 'Back',
    nextButtonTabIndex = 1,
    isFirstCard,
    children,
    ...props
}: GoalCardLayoutProps) => {
    return (
        <chakra.form
            onSubmit={(e) => {
                e.preventDefault();
                onNext();
            }}
            h={'full'}
        >
            <FlexCol
                h="full"
                px={6}
                pb={6}
                pt={9}
                position="relative"
                {...props}
            >
                <Button
                    position="absolute"
                    variant="ghost"
                    colorScheme="gray"
                    size="xs"
                    w={7}
                    minW={7}
                    h={7}
                    top={2}
                    right={2}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    onClickEvent="Goal Card Close Button Clicked"
                    tabIndex={-1}
                >
                    <XClose size="sm" />
                </Button>
                <FlexRow grow={0}>
                    <GoalIconCircle type={type} mr={3} />
                    <FlexCol justifyContent="center">
                        <Subheader pr={2}>{title}</Subheader>
                        {subtitle && (
                            <Paragraph fontSize="sm" color="textMuted" mt={3}>
                                {subtitle}
                            </Paragraph>
                        )}
                    </FlexCol>
                </FlexRow>
                <FlexCol grow={1} justifyContent="center">
                    {children}
                </FlexCol>
                <FlexRow justify="space-between" align="center" grow={0}>
                    <div>
                        {onBack && !isFirstCard && (
                            <ButtonWithIcon
                                ml={-2}
                                variant="ghost"
                                colorScheme="gray"
                                iconLeft={<ArrowLeft />}
                                onClick={() => onBack()}
                                onClickEvent="Goal Card Back Button Clicked"
                                onClickEventParams={{ title }}
                                tabIndex={nextButtonTabIndex + 1}
                            >
                                {backButtonText}
                            </ButtonWithIcon>
                        )}
                    </div>
                    <Button
                        type="submit"
                        isDisabled={isNextDisabled}
                        isLoading={isNextLoading}
                        tabIndex={nextButtonTabIndex}
                        onClickEvent="Goal Card Next Button Clicked"
                        onClickEventParams={{ title }}
                    >
                        {nextButtonText}
                    </Button>
                </FlexRow>
            </FlexCol>
        </chakra.form>
    );
};

export default GoalCardLayout;
